import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HexaForm } from "./hexaforms-react";
import { COPY, type Lang, type ProductStatus } from "./landingCopy";

const ACCENT = "#7C3AED";

// Botones: el morado de marca solo para la accion principal; el resto, secundario.
const BTN_PRIMARY =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-[#7C3AED] px-6 py-3 text-sm font-medium text-white shadow-sm shadow-violet-900/10 transition hover:bg-[#6D28D9] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7C3AED]";
const BTN_SECONDARY =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl border border-slate-300 bg-white px-6 py-3 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:text-slate-900";

// scripts/prerender.js marca la pagina al generar el HTML estatico: el formulario
// se monta solo en el navegador real (evita vistas falsas y HTML de error en el snapshot).
const IS_PRERENDER = typeof window !== "undefined" && Boolean((window as { __PRERENDER__?: boolean }).__PRERENDER__);

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur">
      {children}
    </span>
  );
}

function StatusBadge({ status, label }: { status: ProductStatus; label: string }) {
  const dots: Record<ProductStatus, string> = {
    Available: "bg-emerald-500",
    "Now launching": "bg-violet-500",
    "Phase II": "bg-emerald-500",
    "Early access": "bg-amber-500",
    "In development": "bg-sky-500",
    Open: "bg-slate-400",
    Internal: "bg-slate-400",
  };
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-xs font-medium text-slate-600">
      <span className={`h-1.5 w-1.5 rounded-full ${dots[status]}`} aria-hidden />
      {label}
    </span>
  );
}

const SIMULATION_PRODUCTS = ["StrataSynth", "QualiSynth", "ArenaSynth"];

const SECTION_EYEBROW = "text-xs font-semibold uppercase tracking-[0.2em] text-slate-500";

// Enlace de texto secundario (flecha incluida).
function TextLink({ href, children, external = false }: { href: string; children: React.ReactNode; external?: boolean }) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 underline-offset-4 transition hover:text-slate-900 hover:underline"
    >
      {children} <span aria-hidden>→</span>
    </a>
  );
}

function ProductCard({
  name,
  image,
  imageAlt,
  tag,
  description,
  status,
  statusLabel,
  bullets,
  ctaLabel,
  ctaHref,
  layerColor,
}: {
  name: string;
  image?: string;
  imageAlt: string;
  tag: string;
  description: string;
  status: ProductStatus;
  statusLabel: string;
  bullets: string[];
  ctaLabel: string;
  ctaHref: string;
  layerColor: string;
}) {
  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-900/5">
      {image && ctaHref !== "#" && (
        <a href={ctaHref} target="_blank" rel="noopener noreferrer" tabIndex={-1} className="mb-5 block overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
          <img src={image} alt={`${name} ${imageAlt}`} width={960} height={510} loading="lazy" decoding="async" className="block aspect-[96/51] h-auto w-full object-cover object-top" />
        </a>
      )}
      <div className={`text-xs font-semibold uppercase tracking-[0.18em] ${layerColor}`}>{tag}</div>
      <div className="mt-3 flex items-start justify-between gap-3">
        <h3 className="text-2xl font-semibold tracking-tight text-slate-950">{name}</h3>
        <StatusBadge status={status} label={statusLabel} />
      </div>
      <p className="mt-3 text-sm leading-6 text-slate-600">{description}</p>
      {bullets.length > 0 && (
        <ul className="mt-5 space-y-2">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-3 text-sm text-slate-700">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-900" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
      {ctaHref !== "#" && (
        <div className="mt-6">
          <a
            href={ctaHref}
            target={ctaHref.startsWith("http") ? "_blank" : undefined}
            rel={ctaHref.startsWith("http") ? "noopener noreferrer" : undefined}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-900 transition hover:border-[#7C3AED] hover:text-[#6D28D9]"
          >
            {ctaLabel} →
          </a>
        </div>
      )}
    </div>
  );
}

export default function EreaceLandingMock({ lang = "en" }: { lang?: Lang }) {
  const t = COPY[lang];
  const [menuOpen, setMenuOpen] = useState(false);
  const [partnershipSubmitted, setPartnershipSubmitted] = useState(false);

  // La navegacion cliente entre / y /es/ no recarga index.html: sincroniza idioma, titulo y description.
  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = t.meta.title;
    document.querySelector('meta[name="description"]')?.setAttribute("content", t.meta.description);
  }, [lang, t]);

  const navItems = [
    { href: "#uses", label: t.header.nav.uses },
    { href: "#products", label: t.header.nav.products },
    { href: "#how", label: t.header.nav.how },
    { href: "#founder", label: t.header.nav.founder },
    { href: "#partners", label: t.header.nav.partners },
    { href: "#faq", label: t.header.nav.faq },
  ];
  const otherLang: Lang = lang === "en" ? "es" : "en";
  const otherLangPath = otherLang === "es" ? "/es/" : "/";
  const d = t.hero.diagram;

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Subtle background gradient */}
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top,_rgba(124,58,237,0.06),_transparent_30%),radial-gradient(circle_at_80%_20%,_rgba(16,185,129,0.05),_transparent_25%)]" />

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/85 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-5 py-3 sm:px-6 sm:py-4 lg:px-8">
          <div className="flex items-center gap-3">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-2xl text-sm font-bold text-white shadow-sm"
              style={{ backgroundColor: ACCENT }}
            >
              E
            </div>
            <div>
              <div className="text-sm font-semibold tracking-[0.18em] text-slate-900">EREACE</div>
              <div className="hidden text-xs text-slate-500 sm:block">{t.header.tagline}</div>
            </div>
          </div>

          <nav className="hidden items-center gap-7 text-sm text-slate-600 md:flex">
            {navItems.map((item) => (
              <a key={item.href} href={item.href} className="transition hover:text-slate-900">{item.label}</a>
            ))}
          </nav>

          <div className="flex items-center gap-1 sm:gap-2">
            <Link
              to={otherLangPath}
              hrefLang={otherLang}
              title={t.header.switchTitle}
              className="rounded-2xl px-3 py-2 text-sm font-medium text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
            >
              {t.header.switchLabel}
            </Link>
            <a href="#partners" className={`${BTN_SECONDARY} px-4 py-2`}>
              {t.header.talk}
            </a>
            <button
              type="button"
              aria-expanded={menuOpen}
              aria-label={t.header.toggleMenu}
              className="rounded-2xl p-2 text-slate-600 hover:bg-slate-100 md:hidden"
              onClick={() => setMenuOpen((o) => !o)}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {menuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
              </svg>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-slate-200 bg-white/95 backdrop-blur md:hidden">
            <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-6 py-4">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="rounded-lg py-2.5 text-sm text-slate-700 hover:bg-slate-100"
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main>
        {/* ── HERO ── */}
        <section id="top" className="mx-auto max-w-7xl px-6 pb-16 pt-12 sm:pb-20 sm:pt-20 lg:px-8 lg:pt-24">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-14">
            <div>
              <div className="mb-6 inline-flex rounded-full border border-slate-200 bg-white px-4 py-2 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-600 shadow-sm sm:text-xs sm:tracking-[0.18em]">
                {t.hero.eyebrow}
              </div>
              <h1 className="max-w-4xl text-[2.25rem] font-semibold leading-[1.08] tracking-tight text-slate-950 sm:text-5xl lg:text-[3.25rem]">
                {t.hero.title}
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg sm:leading-8">
                {t.hero.body}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 sm:mt-10">
                <a href="#uses" className={BTN_PRIMARY}>
                  {t.hero.ctaPrimary}
                </a>
                <TextLink href="#products">{t.hero.ctaSecondary}</TextLink>
              </div>
            </div>

            {/* Jerarquia: StrataSynth (motor) -> QualiSynth y ArenaSynth */}
            <div className="rounded-[28px] border border-slate-200 bg-white p-3 shadow-2xl shadow-slate-900/5 sm:rounded-[32px] sm:p-5">
              <div className="rounded-[22px] bg-slate-50 p-4 sm:rounded-[28px] sm:p-6">
                <div className="mb-4 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{d.systemLabel}</div>
                <div className="rounded-3xl border border-indigo-200 bg-indigo-50 p-5">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-700">{d.engineLabel}</div>
                  <div className="mt-1.5 text-2xl font-semibold text-slate-900">StrataSynth</div>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{d.engineText}</p>
                  <div className="mt-3 text-xs font-medium text-indigo-700/80">{d.engineAccess}</div>
                </div>
                <div className="flex justify-center" aria-hidden>
                  <div className="h-6 w-px bg-slate-300" />
                </div>
                <div className="text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{d.appsLabel}</div>
                <div className="flex justify-center" aria-hidden>
                  <div className="h-4 w-px bg-slate-300" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:rounded-3xl">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700 sm:text-xs">{d.qualisynthLabel}</div>
                    <div className="mt-1 text-base font-semibold text-slate-900 sm:text-lg">QualiSynth</div>
                    <p className="mt-1 text-sm leading-5 text-slate-600">{d.qualisynthText}</p>
                  </div>
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 sm:rounded-3xl">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-rose-700 sm:text-xs">{d.arenasynthLabel}</div>
                    <div className="mt-1 text-base font-semibold text-slate-900 sm:text-lg">ArenaSynth</div>
                    <p className="mt-1 text-sm leading-5 text-slate-600">{d.arenasynthText}</p>
                  </div>
                </div>
                <div className="mt-5 border-t border-slate-200 pt-4">
                  <div className="text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{d.layersLabel}</div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {[
                      { label: d.layerCapture, name: "HexaForms", tone: "border-emerald-200 text-emerald-700" },
                      { label: d.layerAudience, name: "InsightPulse", tone: "border-orange-200 text-orange-700" },
                      { label: d.layerMemory, name: "LuminoraCore", tone: "border-violet-200 text-violet-700" },
                    ].map((layer) => (
                      <div key={layer.name} className={`rounded-2xl border bg-white px-2 py-2.5 text-center ${layer.tone}`}>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.14em]">{layer.label}</div>
                        <div className="mt-0.5 text-[13px] font-semibold text-slate-900">{layer.name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── USOS: un motor, tres formas de usarlo ── */}
        <section id="uses" className="border-y border-slate-200 bg-slate-50/70">
          <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20 lg:px-8">
            <div className={SECTION_EYEBROW}>{t.uses.eyebrow}</div>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">{t.uses.title}</h2>
            <div className="mt-10 grid gap-5 lg:grid-cols-3">
              {t.uses.items.map((item) => (
                <div key={item.verb} className="flex flex-col rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#6D28D9]">{item.verb}</div>
                  <p className="mt-3 text-lg font-semibold leading-7 text-slate-950">{item.lead}</p>
                  <div className="mt-5 border-t border-slate-200 pt-5">
                    <div className="text-base font-semibold text-slate-900">{item.product}</div>
                    <p className="mt-1.5 text-sm leading-6 text-slate-600">{item.productLine}</p>
                  </div>
                  <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-3 lg:mt-auto lg:pt-6">
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-900 transition hover:border-[#7C3AED] hover:text-[#6D28D9]"
                    >
                      {item.cta} →
                    </a>
                    <TextLink href={item.tryHref} external>{item.tryLabel}</TextLink>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── PRODUCTS ── */}
        <section id="products" className="mx-auto max-w-7xl px-6 py-16 sm:py-20 lg:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className={SECTION_EYEBROW}>{t.productsSection.eyebrow}</div>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                {t.productsSection.title}
              </h2>
            </div>
            <p className="max-w-xl text-base leading-7 text-slate-600">
              {t.productsSection.body}
            </p>
          </div>

          <h3 className="mt-12 text-sm font-semibold uppercase tracking-[0.16em] text-slate-900">{t.productsSection.simulationTitle}</h3>
          <div className="mt-5 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {t.products.filter((product) => SIMULATION_PRODUCTS.includes(product.name)).map((product) => (
              <ProductCard
                key={product.name}
                name={product.name}
                image={product.image}
                imageAlt={t.header.imageAlt}
                tag={product.tag}
                description={product.description}
                status={product.status}
                statusLabel={t.statusLabels[product.status]}
                bullets={product.bullets}
                ctaLabel={product.ctaLabel}
                ctaHref={product.ctaHref}
                layerColor={product.layerColor}
              />
            ))}
          </div>

          <div className="mt-14 border-t border-slate-200 pt-10">
            <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-900">{t.productsSection.otherTitle}</h3>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">{t.productsSection.otherBody}</p>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {t.products.filter((product) => !product.internal && !SIMULATION_PRODUCTS.includes(product.name)).map((product) => (
                <div key={product.name} className="flex flex-col rounded-3xl border border-slate-200 bg-white p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${product.layerColor}`}>{product.tag}</div>
                      <div className="mt-1.5 text-lg font-semibold text-slate-950">{product.name}</div>
                    </div>
                    <StatusBadge status={product.status} label={t.statusLabels[product.status]} />
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{product.description.split(".")[0]}.</p>
                  <div className="mt-4 md:mt-auto md:pt-4">
                    <TextLink href={product.ctaHref} external>{product.ctaLabel}</TextLink>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── COMO FUNCIONA: ciclo ── */}
        <section id="how" className="border-y border-slate-200 bg-slate-50/70">
          <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20 lg:px-8">
            <div className={SECTION_EYEBROW}>{t.how.eyebrow}</div>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">{t.how.title}</h2>
            <ol className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {t.how.steps.map((step, index) => (
                <li key={step.title} className="relative rounded-3xl border border-slate-200 bg-white p-5">
                  <div className="text-xs font-semibold tabular-nums text-slate-400">0{index + 1}</div>
                  <div className="mt-1 text-lg font-semibold text-slate-950">{step.title}</div>
                  <p className="mt-1.5 text-sm leading-6 text-slate-600">{step.text}</p>
                  {index < t.how.steps.length - 1 && (
                    <span
                      aria-hidden
                      className="absolute -right-[11px] top-1/2 z-10 hidden h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-[10px] text-slate-400 lg:flex"
                    >
                      →
                    </span>
                  )}
                </li>
              ))}
            </ol>
            <p className="mt-5 text-sm font-medium text-slate-700">{t.how.loop}</p>

          </div>
        </section>

        {/* ── FOUNDER ── */}
        <section id="founder" className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div className="space-y-5">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{t.founder.eyebrow}</div>
              <h2 className="text-3xl font-semibold tracking-tight text-slate-950">{t.founder.title}</h2>
              <p className="text-slate-600">
                {t.founder.greetingBefore}<span className="font-semibold text-slate-900">Ruly Altamirano</span>{t.founder.greetingAfter}
              </p>
              {t.founder.paragraphs.map((paragraph) => (
                <p key={paragraph} className="text-sm leading-relaxed text-slate-600">
                  {paragraph}
                </p>
              ))}
              <div className="flex flex-wrap gap-2">
                {t.founder.pills.map((pill) => <Pill key={pill}>{pill}</Pill>)}
              </div>
            </div>
            <div className="rounded-[32px] border border-slate-200 bg-white p-8 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">{t.founder.cardTitle}</h3>
              <div className="mt-6 space-y-4 text-sm leading-relaxed text-slate-600">
                {t.founder.cardItems.map((item) => (
                  <p key={item.name}>
                    <strong className="text-slate-900">{item.name}</strong>{item.text}
                  </p>
                ))}
              </div>
              <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <div className="text-sm font-semibold text-slate-900">{t.founder.coreTitle}</div>
                <p className="mt-2 text-base leading-7 text-slate-700">
                  {t.founder.coreMessage}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── PARTNERSHIPS ── */}
        <section id="partners" className="border-t border-slate-200 bg-slate-50/60">
          <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
            <div className="grid gap-12 lg:grid-cols-2">
              <div className="space-y-6">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{t.partners.eyebrow}</div>
                <h2 className="text-3xl font-semibold tracking-tight text-slate-950">{t.partners.title}</h2>
                <p className="text-base leading-7 text-slate-600">
                  {t.partners.body}
                </p>
                <div className="space-y-4">
                  <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-slate-900">{t.partners.yesTitle}</span>
                      <span className="rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">{t.partners.yesBadge}</span>
                    </div>
                    <ul className="mt-3 space-y-2 text-sm text-slate-600">
                      {t.partners.yes.map((item) => <li key={item}>• {item}</li>)}
                    </ul>
                  </div>
                  <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-slate-900">{t.partners.noTitle}</span>
                      <span className="rounded-full border border-red-200 bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">{t.partners.noBadge}</span>
                    </div>
                    <ul className="mt-3 space-y-2 text-sm text-slate-600">
                      {t.partners.no.map((item) => <li key={item}>• {item}</li>)}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">{t.partners.formTitle}</h3>
                    <p className="mt-1 text-sm text-slate-600">{t.partners.formSubtitle}</p>
                  </div>
                  {partnershipSubmitted ? (
                    <span className="rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">{t.partners.sent}</span>
                  ) : (
                    <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">{t.partners.selective}</span>
                  )}
                </div>
                <div className="my-6 h-px w-full bg-slate-200" />
                {!IS_PRERENDER && (
                  <HexaForm
                    formId="form_a9261fcbff16"
                    language={lang}
                    apiKey="hf_kJyVbADoHf5XprKTs-wtll3waokkEjRgC4jMWXLj2fw"
                    theme={{
                      backgroundColor: "transparent",
                      textColor: "#0f172a",
                      labelColor: "#475569",
                      inputBackgroundColor: "#ffffff",
                      borderColor: "#cbd5e1",
                      placeholderColor: "#64748b",
                      primaryColor: ACCENT,
                      buttonStyle: "rounded",
                    }}
                    loading={
                      <div className="flex min-h-[18rem] items-center justify-center text-sm text-slate-500" role="status">
                        {t.partners.formLoading}
                      </div>
                    }
                    errorFallback={
                      <p className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm leading-6 text-slate-600" role="alert">
                        {t.partners.formErrorBefore}
                        <a href="mailto:info@ereace.es" className="font-medium text-slate-900 underline underline-offset-4">info@ereace.es</a>
                        {t.partners.formErrorAfter}
                      </p>
                    }
                    onSuccess={() => {
                      setPartnershipSubmitted(true);
                      setTimeout(() => setPartnershipSubmitted(false), 5000);
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section id="faq" className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <div className="mb-12">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{t.faq.eyebrow}</div>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950">{t.faq.title}</h2>
            <p className="mt-3 max-w-2xl text-base text-slate-600">
              {t.faq.body}
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {t.faq.items.map((faq) => (
              <div key={faq.q} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="text-sm font-semibold text-slate-900">{faq.q}</div>
                <div className="mt-3 text-sm leading-relaxed text-slate-600">{faq.a}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── CLOSING CTA ── */}
        <section className="mx-auto max-w-7xl px-6 pb-20 lg:px-8">
          <div className="rounded-[36px] border border-slate-200 bg-slate-950 px-8 py-14 text-white shadow-2xl shadow-slate-900/10 lg:px-12 lg:py-16">
            <div className="grid gap-8 lg:grid-cols-[1fr_0.75fr] lg:items-end">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{t.closing.eyebrow}</div>
                <h2 className="mt-4 max-w-3xl text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                  {t.closing.title}
                </h2>
                <p className="mt-5 max-w-2xl text-base leading-7 text-slate-300">
                  {t.closing.body}
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <a href="#partners" className={BTN_PRIMARY}>
                    {t.closing.talk}
                  </a>
                  <a
                    href="#uses"
                    className="rounded-2xl border border-white/20 bg-white/10 px-6 py-3 text-sm font-medium text-white transition hover:bg-white/20"
                  >
                    {t.closing.explore}
                  </a>
                </div>
              </div>
              <div className="rounded-[28px] border border-white/10 bg-white/5 p-6">
                <div className="text-sm font-medium text-slate-300">{t.closing.loopTitle}</div>
                <div className="mt-4 space-y-2 text-sm text-slate-400">
                  {t.closing.loop.map((step, i) => (
                    <div key={step} className="flex items-center gap-3">
                      <span className={`h-1.5 w-1.5 rounded-full ${["bg-amber-400", "bg-indigo-400", "bg-rose-400"][i]}`} />
                      <span>{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-10 lg:px-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="font-semibold text-slate-900">Ereace</div>
              <div className="mt-1 text-xs text-slate-500">{t.footer.tagline}</div>
            </div>
            <div className="flex flex-wrap items-center gap-5 text-sm text-slate-600">
              {navItems.slice(0, 5).map((item) => (
                <a key={item.href} href={item.href} className="hover:text-slate-900">{item.label}</a>
              ))}
              <a href="mailto:info@ereace.es" className="hover:text-slate-900">info@ereace.es</a>
              <a href="/privacy-policy" className="hover:text-slate-900">{t.footer.privacy}</a>
            </div>
          </div>
          <div
            className="mt-6 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-slate-500"
            aria-label="Herramientas relacionadas"
          >
            <span className="text-slate-400">Tools</span>
            <span className="text-slate-300" aria-hidden>
              ·
            </span>
            <a
              href="https://clavixor.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-700"
            >
              Clavixor
            </a>
            <span className="text-slate-300" aria-hidden>
              ·
            </span>
            <a
              href="https://domiquo.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-700"
            >
              Domiquo
            </a>
          </div>
          <div className="mt-4 text-xs text-slate-500">© {new Date().getFullYear()} Ereace. {t.footer.rights}</div>
        </div>
      </footer>
    </div>
  );
}
